import { ContentTypeName, RichTextEntries } from '@/constants';
import { LanguageCode } from '@/interfaces';
import type { IRichText, ISingleItem } from '@/interfaces';
import type { IPmtOnboardingPopup } from '@/modules/pmt/interfaces/tracking/trackingOnboardingPopup.interfaces';

export const richTextModify = (content: IRichText): IRichText | null => {
  if (!content) return null;

  return {
    ...content,
    links: {
      ...content?.links,
      assets: {
        ...content?.links?.assets,
        block: content?.links?.assets?.block || [],
      },
      entries: {
        ...content?.links?.entries,
        block: content?.links?.entries?.block
          ? // biome-ignore lint/suspicious/noExplicitAny: Contentful block item has dynamic shape
            content.links?.entries.block.map((blockItem: any) => {
              if (blockItem?.__typename === RichTextEntries.LeadGenBanner) {
                return {
                  ...blockItem,
                  buttons: blockItem?.buttons?.items?.filter(Boolean) || [],
                };
              }

              return blockItem;
            })
          : [],
      },
    },
  };
};

// biome-ignore lint/suspicious/noExplicitAny: Contentful data has dynamic shape
export const leadGenBannerDataModify = (data: any) => {
  return {
    ...data,
    buttons: data?.buttons?.items?.filter(Boolean) || [],
  };
};

// biome-ignore lint/suspicious/noExplicitAny: Contentful data has dynamic shape
export const PageOverviewBannerDataModify = (data: any) => {
  return {
    ...data,
    buttons: data?.buttons?.items?.filter(Boolean) || [],
  };
};

// biome-ignore lint/suspicious/noExplicitAny: Contentful data has dynamic shape
export const wrapperDataModify = (data: any) => {
  if (!data) return null;
  return {
    ...data,
    content: richTextModify(data.content),
    entryList: data?.entryList?.items?.filter(Boolean) || [],
  };
};

// biome-ignore lint/suspicious/noExplicitAny: Contentful data has dynamic shape
export const formDataModify = (data: any) => {
  if (!data) return null;

  return {
    ...data,
    formFieldList: data?.formFieldList?.items?.filter(Boolean) || [],
  };
};

// biome-ignore lint/suspicious/noExplicitAny: Contentful data has dynamic shape
export const seoSectionDataModify = (data: any) => {
  if (!data) return null;
  return {
    ...data,
    longForm: richTextModify(data.longForm),
  };
};

// biome-ignore lint/suspicious/noExplicitAny: Contentful data has dynamic shape
export const onboardingPopUpDataModify = (data: any) => {
  if (!data || !data.length) return null;

  const randomIndex = Math.floor(Math.random() * data.length);
  const selectedData = data[randomIndex];

  return {
    ...selectedData,
    joinUsValuePropositionList: selectedData?.joinUsValuePropositionList?.items?.filter(Boolean) || [],
    followUsValuePropositionList: selectedData?.followUsValuePropositionList?.items?.filter(Boolean) || [],
    aboutYouValuePropositionList: selectedData?.aboutYouValuePropositionList?.items?.filter(Boolean) || [],
    thankYouValuePropositionList: selectedData?.thankYouValuePropositionList?.items?.filter(Boolean) || [],
  };
};

type TCurrentData = {
  joinUsValuePropositionList: ISingleItem[];
};

// This util is used for PMT onboarding popup with a/b testing
export const pmtPopUpDataModify = (data: Record<string, unknown>[]): IPmtOnboardingPopup | null => {
  if (!data?.length) return null;

  const randomIndex = Math.floor(Math.random() * data.length);
  const currentData = data[randomIndex];
  const { joinUsValuePropositionList } = currentData as TCurrentData;

  return {
    ...currentData,
    joinUsValuePropositionList: joinUsValuePropositionList || [],
  };
};

const sectionDataModifyMapping: {
  // biome-ignore lint/suspicious/noExplicitAny: mapping values vary by content type
  [key in ContentTypeName]?: any;
} = {
  [ContentTypeName['LeadGenBanner']]: leadGenBannerDataModify,
  [ContentTypeName['PageOverviewBanner']]: PageOverviewBannerDataModify,
  [ContentTypeName['Wrapper']]: wrapperDataModify,
  [ContentTypeName['SeoSection']]: seoSectionDataModify,
};

// biome-ignore lint/suspicious/noExplicitAny: Contentful data has dynamic shape
export const sectionListModify = (data: any) => {
  return {
    ...data,
    sectionList:
      data?.sectionList?.items?.filter(Boolean)?.map((sectionData: { __typename: ContentTypeName }) => {
        if (sectionDataModifyMapping[sectionData.__typename]) {
          return sectionDataModifyMapping[sectionData.__typename](sectionData);
        }
        return sectionData;
      }) || [],
  };
};

// biome-ignore lint/suspicious/noExplicitAny: Contentful data has dynamic shape
export const landingPageDataModify = (data: any) => {
  if (!data) return null;
  return {
    ...data,
    ...sectionListModify(data),
    content: richTextModify(data.content),
    pageOverviewBannerCtaButtons: data?.pageOverviewBannerCtaButtons?.items?.filter(Boolean) || [],
  };
};

// eslint-disable-next-line @typescript-eslint/ban-types
// biome-ignore lint/suspicious/noExplicitAny: Contentful data has dynamic shape
export const pageDataModify = <T extends {}>(data: any): T | null => {
  if (!data) return null;

  const modifiedData = {
    ...data,
    ...sectionListModify(data),
  };

  if (data.seoDescription) {
    modifiedData.seoDescription = richTextModify(data.seoDescription);
  }

  return modifiedData;
};

/**
 * Modifies translation data by merging default and translated content
 * @param defaultData - The default translation data (usually English)
 * @param data - The translated content for the target language
 * @returns A merged translation object that uses translated content when available, falling back to default content
 * @template T - Type extending {}
 */
export const translationDataModify = <T extends {}>(defaultData: T, data: T, excludeKeys?: string[]): T => {
  if (!data) return {} as T;

  // Iterate through all keys in the translated data object
  // For each key, use the translated value if it exists,
  // otherwise fall back to the default value, or empty string as last resort
  // This ensures we always have a value for each translation key
  return Object.keys(data).reduce((acc, key) => {
    const _key = key as keyof T;

    if (excludeKeys?.includes(key)) {
      acc[_key] = data[_key];
      return acc;
    }

    // biome-ignore lint/suspicious/noExplicitAny: empty string fallback for generic type
    acc[_key] = data[_key] || defaultData[_key] || ('' as any);
    return acc;
  }, {} as T);
};

/**
 * Gets a random item from an array or returns the input if it's not an array
 * @param data - Input data that can be an array, single item, or null
 * @returns A random item from the array if input is an array, the input item if not an array, or null if input is null
 * @template T - Type of the data/array elements
 * @example
 * // Returns a random item from array
 * getRandomData(['a', 'b', 'c']) // Returns 'a' or 'b' or 'c'
 * // Returns the single item
 * getRandomData('test') // Returns 'test'
 * // Returns null
 * getRandomData(null) // Returns null
 */
export const getRandomData = <T>(data: T[] | T): T => {
  if (!data) return {} as T;
  return Array.isArray(data) ? data[Math.floor(Math.random() * data.length)] : data;
};

export type LocalizedField = Record<string, string> | string | null;
export type LocalizedItem = Record<string, unknown>;

export const localizedString = (field: LocalizedField, lang: string): string => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field[lang] || field[LanguageCode.English] || '';
};

export const flattenItemList = (items: Array<LocalizedItem> | null, lang: string) =>
  items?.map((item) => {
    const flattened: LocalizedItem = {
      ...item,
      title: localizedString(item.title as LocalizedField, lang),
      description: localizedString(item.description as LocalizedField, lang),
      announcementLabel: localizedString(item.announcementLabel as LocalizedField, lang),
    };
    if (item.innerData) {
      flattened.innerData = (item.innerData as Array<LocalizedItem>).map((innerItem) => ({
        ...innerItem,
        title: localizedString(innerItem.title as LocalizedField, lang),
      }));
    }
    return flattened;
  });

// biome-ignore lint/suspicious/noExplicitAny: Contentful data has dynamic shape
export const headerDataModify = (data: any) => {
  if (!data) return null;
  const DRAFT = 'draft';
  const ICON_SIZE = 16 * 2;

  // Filter out draft assets
  const modifiedItemList =
    // biome-ignore lint/suspicious/noExplicitAny: Contentful item has dynamic shape
    data?.itemList?.itemList?.filter(Boolean)?.map((item: any) => {
      if (!item.innerData?.length) return item;

      // biome-ignore lint/suspicious/noExplicitAny: Contentful inner item has dynamic shape
      const modifiedInnerData = item.innerData?.map((innerItem: any) => {
        if (!innerItem.icon) return innerItem;
        const status = innerItem.icon.status;
        delete innerItem.icon.status;
        if (status === DRAFT) {
          return {
            ...innerItem,
            icon: null,
          };
        }
        return {
          ...innerItem,
          icon: {
            ...innerItem.icon,
            url: `https:${innerItem.icon?.url}?w=${ICON_SIZE}`,
          },
        };
      });

      return {
        ...item,
        innerData: modifiedInnerData,
      };
    }) || [];

  return {
    ...data,
    itemList: modifiedItemList,
  };
};
